import { ORIENTATION } from "../model/Constants";
import Area from "./Area";

export function debounce(func: Function, wait: number, immediate?: boolean) {
    let timeout: any;

    return function executedFunction() {
        const context = this;
        const args = arguments;

        const later = function () {
            timeout = null;

            if (!immediate) {
                func.apply(context, args);
            }
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);

        timeout = setTimeout(later, wait);

        if (callNow) {
            func.apply(context, args);
        }
    };
}

export function log(msg: any): void {
    console.log(`Messaging-UI:${msg}`);
}

export function isNullOrUndefined(val: any): boolean {
    return val == null || typeof (val) === "undefined";
}

export function attachListener(target: any, type: string, listener: any, useCapture = false) {
    if (target.addEventListener) {
        target.addEventListener(type, listener, useCapture);
    } else if (target.attachEvent) {
        target.attachEvent(`on${type}`, listener);
    }
}

export function detachListener(target: any, type: string, listener: any, useCapture = false): void {
    if (target.removeEventListener) {
        target.removeEventListener(type, listener, useCapture);
    } else if (target.attachEvent) {
        target.detachEvent(`on${type}`, listener);
    }
}

export function isIOS(): boolean {
    return /(iPad|iPhone|iPod)/g.test(window.navigator.userAgent);
}

export function getOSType(): string {
    let os = "Unknown";
    const platform = window.navigator.platform;
    const ua = window.navigator.userAgent;

    if (/Android/.test(ua)) {
        os = "Android";
    } else if (/iPhone/.test(ua) || /iPad/.test(ua) || /iPod/.test(ua)) {
        os = "iOS";
    } else if (/Win/.test(platform)) {
        os = "Windows";
    } else if (/Linux/.test(platform)) {
        os = "Linux";
    } else if (/Mac/.test(platform)) {
        os = "Mac OS";
    }

    return os;
}

export function isAndroidWebView(): boolean {
    const pattern = new RegExp("Android.*?(wv|Version\\/[.0-9]+).*?Chrome\\/[0-9]{2}", "i");
    return pattern.test(window.navigator.userAgent);
}

export function isChrome(): boolean {
    return (window.navigator.userAgent.indexOf("Chrome") >= 0 || window.navigator.userAgent.indexOf("CriOS") >= 0) && (!isAndroidWebView()); // navigator.userAgent for Edge have "Chrome" inside the value
}

export function isIE(): boolean {
    return window.navigator.appName == "Microsoft Internet Explorer" || window.navigator.userAgent.indexOf("Trident") != -1;
}

export class Point {
    p1: any;

    p2: any;

    constructor(p1?: number, p2?: number) {
        this.p1 = p1;
        this.p2 = p2;
    }
}

export function loadScript(src: string): void {
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    document.body.appendChild(script);
}

export function getIphoneModel() {
    // Create a canvas element which can be used to retrieve information about the GPU.
    const canvas = document.createElement("canvas");
    let renderer = "";
    let result = "";

    if (canvas) {
        const context: any = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

        if (context) {
            const info = context.getExtension("WEBGL_debug_renderer_info");

            if (info) {
                renderer = context.getParameter(info.UNMASKED_RENDERER_WEBGL);
            }
        }
    }

    if (window.parent.screen.height / window.parent.screen.width == 896 / 414 && window.devicePixelRatio == 3) {
        switch (renderer) {
            case "Apple A12 GPU":
                result = "iPhoneXSMax";
                break;

            default:
                result = "iPhoneXSMax";
        }
    } else if (window.parent.screen.height / window.parent.screen.width == 896 / 414 && window.devicePixelRatio == 2) {
        switch (renderer) {
            case "Apple A12 GPU":
                result = "iPhoneXR";
                break;

            default:
                result = "iPhoneXR";
        }
    } else if (window.parent.screen.height / window.parent.screen.width == 812 / 375 && window.devicePixelRatio == 3) {
        switch (renderer) {
            case "Apple A12 GPU":
                result = "iPhoneXS_XSMaxZoomed";
                break;

            case "Apple A11 GPU":
                result = "iPhoneX";
                break;

            default:
                result = "iPhoneX";
        }
    } else if (window.parent.screen.height / window.parent.screen.width == 812 / 375 && window.devicePixelRatio == 2) {
        switch (renderer) {
            case "Apple A12 GPU":
                result = "iPhoneXRZoomed";
                break;

            default:
                result = "iPhoneXRZoomed";
        }
    } else if ((window.parent.screen.height / window.parent.screen.width == 736 / 414) && (window.devicePixelRatio == 3)) { // iPhone 6+/6s+/7+ and 8+
        switch (renderer) {
            case "Apple A8 GPU":
                result = "iPhone6Plus";
                break;

            case "Apple A9 GPU":
                result = "iPhone6sPlus";
                break;

            case "Apple A10 GPU":
                result = "iPhone7Plus";
                break;

            case "Apple A11 GPU":
                result = "iPhone8Plus";
                break;

            default:
                result = "iPhone6Plus";
        }
    } else if ((window.parent.screen.height / window.parent.screen.width == 667 / 375) && (window.devicePixelRatio == 3)) { // iPhone 6+/6s+/7+ and 8+ in zoom mode
        switch (renderer) {
            case "Apple A8 GPU":
                result = "iPhone6PlusZoomed";
                break;

            case "Apple A9 GPU":
                result = "iPhone6sPlusZoomed";
                break;

            case "Apple A10 GPU":
                result = "iPhone7PlusZoomed";
                break;

            case "Apple A11 GPU":
                result = "iPhone8PlusZoomed";
                break;

            default:
                result = "iPhone6PlusZoomed";
        }
    } else if ((window.parent.screen.height / window.parent.screen.width == 667 / 375)
        && (window.devicePixelRatio == 2)) { // iPhone 6/6s/7 and 8
        switch (renderer) {
            case "Apple A8 GPU":
                result = "iPhone6";
                break;

            case "Apple A9 GPU":
                result = "iPhone6s";
                break;

            case "Apple A10 GPU":
                result = "iPhone7";
                break;

            case "Apple A11 GPU":
                result = "iPhone8";
                break;

            default:
                result = "iPhone6";
        }
    } else if ((window.parent.screen.height / window.parent.screen.width == 1.775) && (window.devicePixelRatio == 2)) { // iPhone 5/5C/5s/SE or 6/6s/7 and 8 in zoom mode
        switch (renderer) {
            case "PowerVR SGX 543":
                result = "iPhone5";
                break;

            case "Apple A7 GPU":
                result = "iPhone5s";
                break;

            case "Apple A8 GPU":
                result = "iPhone6Zoomed";
                break;

            case "Apple A9 GPU":
                result = "iPhone6sZoomed";
                break;

            case "Apple A10 GPU":
                result = "iPhone7Zoomed";
                break;

            case "Apple A11 GPU":
                result = "iPhone8Zoomed";
                break;

            default:
                result = "iPhone6Zoomed";
        }
    } else {
        result = "Not an iPhone";
    }

    return result;
}

export function getVisualViewportHeight(): number {
    if (window.parent.visualViewport) {
        const scaledHeight = window.parent.visualViewport.height;
        return Math.round(scaledHeight);
    }
    return 0;
}

/**
 * add or remove aria hidden attribute from iframe element
 * @param {Boolean} add
 * @param {HTMLIFrameElement} el
 */
export function addOrRemoveFrameAria(add: boolean, el: any) {
    if (add) {
        el.setAttribute("aria-hidden", true);
        el.setAttribute("hidden", true);
    } else {
        el.removeAttribute("aria-hidden");
        el.removeAttribute("hidden");
    }
}

export function getZoom(vp: Area, orientation: string): number {
    try {
        const rw = parseInt(orientation == ORIENTATION.PORTRAIT
            ? window.parent.screen.width.toString() : window.parent.screen.height.toString());
        const z = Math.round(100 * (rw / vp.width)) / 100;
        return z;
    } catch (err) {
        return 1;
    }
}
